import React from 'react';
import { notification } from 'antd';
import { 
  CheckCircleOutlined, 
  ExclamationCircleOutlined, 
  InfoCircleOutlined, 
  CloseCircleOutlined,
  ReloadOutlined 
} from '@ant-design/icons';
import { ErrorInfo } from '../../utils/errorHandler';

/**
 * Toast notification types
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Toast configuration interface
 */
export interface ToastConfig {
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  showRetry?: boolean;
  onRetry?: () => void;
  placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
}

/**
 * Toast utility class for showing notifications
 */
export class Toast {
  private static defaultDuration = {
    success: 3,
    info: 4,
    warning: 4.5,
    error: 6,
  };

  /**
   * Show success toast notification
   * @param title Success message title
   * @param message Optional detailed message
   * @param duration Duration in seconds
   */
  public static success(title: string, message?: string, duration?: number): void {
    notification.success({
      message: title,
      description: message,
      duration: duration || Toast.defaultDuration.success,
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      placement: 'topRight',
    });
  }

  /**
   * Show error toast notification
   * @param title Error message title
   * @param message Optional detailed message
   * @param duration Duration in seconds
   * @param onRetry Optional retry callback
   */
  public static error(
    title: string, 
    message?: string, 
    duration?: number, 
    onRetry?: () => void
  ): void {
    const btn = onRetry ? (
      <div style={{ marginTop: 8 }}>
        <button
          className="ant-btn ant-btn-primary ant-btn-sm"
          onClick={() => {
            notification.destroy();
            onRetry();
          }}
          style={{ marginRight: 8 }}
        >
          <ReloadOutlined /> 重试
        </button>
        <button
          className="ant-btn ant-btn-sm"
          onClick={() => notification.destroy()}
        >
          关闭
        </button>
      </div>
    ) : undefined;

    notification.error({
      message: title,
      description: message,
      duration: duration || Toast.defaultDuration.error,
      icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      placement: 'topRight',
      btn,
    });
  }

  /**
   * Show warning toast notification
   * @param title Warning message title
   * @param message Optional detailed message
   * @param duration Duration in seconds
   */
  public static warning(title: string, message?: string, duration?: number): void {
    notification.warning({
      message: title,
      description: message,
      duration: duration || Toast.defaultDuration.warning,
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      placement: 'topRight',
    });
  }

  /**
   * Show info toast notification
   * @param title Info message title
   * @param message Optional detailed message
   * @param duration Duration in seconds
   */
  public static info(title: string, message?: string, duration?: number): void {
    notification.info({
      message: title,
      description: message,
      duration: duration || Toast.defaultDuration.info,
      icon: <InfoCircleOutlined style={{ color: '#1890ff' }} />,
      placement: 'topRight',
    });
  }

  /**
   * Show toast based on configuration
   * @param config Toast configuration
   */
  public static show(config: ToastConfig): void {
    const { type, title, message, duration, showRetry, onRetry, placement } = config;

    const commonProps = {
      message: title,
      description: message,
      duration: duration || Toast.defaultDuration[type],
      placement: placement || 'topRight',
    };

    switch (type) {
      case 'success':
        notification.success({
          ...commonProps,
          icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
        });
        break;

      case 'error':
        const btn = showRetry && onRetry ? (
          <div style={{ marginTop: 8 }}>
            <button
              className="ant-btn ant-btn-primary ant-btn-sm"
              onClick={() => {
                notification.destroy();
                onRetry();
              }}
              style={{ marginRight: 8 }}
            >
              <ReloadOutlined /> 重试
            </button>
            <button
              className="ant-btn ant-btn-sm"
              onClick={() => notification.destroy()}
            >
              关闭
            </button>
          </div>
        ) : undefined;

        notification.error({
          ...commonProps,
          icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
          btn,
        });
        break;

      case 'warning':
        notification.warning({
          ...commonProps,
          icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
        });
        break;

      case 'info':
        notification.info({
          ...commonProps,
          icon: <InfoCircleOutlined style={{ color: '#1890ff' }} />,
        });
        break;
    }
  }

  /**
   * Show error toast from ErrorInfo object
   * @param error Error information
   * @param onRetry Optional retry callback for network errors
   */
  public static showError(error: ErrorInfo, onRetry?: () => void): void {
    let title = '操作失败';
    let showRetryButton = false;

    // Customize title and retry option based on error type
    switch (error.type) {
      case 'network':
        title = '网络错误';
        showRetryButton = true;
        break;
      case 'business':
        title = '业务错误';
        break;
      case 'system':
        title = '系统错误';
        showRetryButton = true;
        break;
      case 'permission':
        title = '权限错误';
        break;
      case 'validation':
        title = '验证错误';
        break;
    }

    let message = error.message;
    if (error.traceId) {
      message += `\n追踪ID: ${error.traceId}`;
    }

    Toast.error(title, message, undefined, showRetryButton ? onRetry : undefined);
  }

  /**
   * Show success toast for common operations
   */
  public static showOperationSuccess(operation: string): void {
    const messages = {
      create: '创建成功',
      update: '更新成功',
      delete: '删除成功',
      save: '保存成功',
      submit: '提交成功',
      enable: '启用成功',
      disable: '禁用成功',
      batch: '批量操作成功',
    };

    Toast.success(messages[operation as keyof typeof messages] || `${operation}成功`);
  }

  /**
   * Show batch operation result toast
   * @param successCount Number of successful operations
   * @param failureCount Number of failed operations
   * @param operation Operation name
   */
  public static showBatchResult(
    successCount: number, 
    failureCount: number, 
    operation: string = '操作'
  ): void {
    if (failureCount === 0) {
      Toast.success(`批量${operation}成功`, `成功处理 ${successCount} 项`);
    } else if (successCount === 0) {
      Toast.error(`批量${operation}失败`, `失败 ${failureCount} 项`);
    } else {
      Toast.warning(
        `批量${operation}部分成功`, 
        `成功 ${successCount} 项，失败 ${failureCount} 项`
      );
    }
  }

  /**
   * Clear all notifications
   */
  public static clear(): void {
    notification.destroy();
  }

  /**
   * Configure global notification settings
   * @param config Global configuration
   */
  public static config(config: {
    placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
    top?: number;
    bottom?: number;
    duration?: number;
    maxCount?: number;
  }): void {
    notification.config(config);
  }
}

/**
 * React hook for using toast notifications
 * @returns Toast utility functions
 */
export function useToast() {
  return {
    success: Toast.success,
    error: Toast.error,
    warning: Toast.warning,
    info: Toast.info,
    show: Toast.show,
    showError: Toast.showError,
    showOperationSuccess: Toast.showOperationSuccess,
    showBatchResult: Toast.showBatchResult,
    clear: Toast.clear,
  };
}

export default Toast;